import React, { useState } from 'react';
import { ShoppingCartIcon, TrendingUpIcon, CalendarIcon, PackageIcon, BarChart3Icon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Pagination } from '../components/ui/Pagination';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
export function SalesPage() {
  const {
    user
  } = useAuth();
  const {
    visits
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  const salesVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => v.venta.huboVenta).filter(v => {
    if (dateFilter) {
      const visitDate = new Date(v.fecha).toISOString().split('T')[0];
      if (visitDate !== dateFilter) return false;
    }
    return true;
  }).sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalVendido = salesVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0);
  const totalPedidos = salesVisits.length;
  const promedioVenta = totalPedidos > 0 ? totalVendido / totalPedidos : 0;
  const today = new Date();
  const todaySales = salesVisits.filter(v => new Date(v.fecha).toDateString() === today.toDateString());
  const todayTotal = todaySales.reduce((sum, v) => sum + v.venta.montoVenta, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short'
  });
  const totalPages = Math.ceil(salesVisits.length / ITEMS_PER_PAGE);
  const paginatedSales = salesVisits.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const salesByVendor = isAdmin ? salesVisits.reduce((acc, v) => {
    if (!acc[v.vendedorNombre]) acc[v.vendedorNombre] = {
      total: 0,
      count: 0
    };
    acc[v.vendedorNombre].total += v.venta.montoVenta;
    acc[v.vendedorNombre].count += 1;
    return acc;
  }, {} as Record<string, {
    total: number;
    count: number;
  }>) : {};
  return <div className="space-y-6" data-id="element-3065">
      <PageHeader title="Registro de Ventas" subtitle="Historial de ventas y facturación" icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-3067" />} data-id="element-3066">
        <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-3068">
          <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-3070" />} data-id="element-3069" />
          {dateFilter && <Button variant="secondary" onClick={() => setDateFilter('')} data-id="element-3071">
              Limpiar
            </Button>}
        </div>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-3072">
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-3074" />} variant="primary" data-id="element-3073" />
        <StatsCard title="Ventas Hoy" value={formatCurrency(todayTotal)} subtitle={`${todaySales.length} pedidos`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-3076" />} variant="success" data-id="element-3075" />
        <StatsCard title="Total Pedidos" value={totalPedidos} icon={<PackageIcon className="w-5 h-5" data-id="element-3078" />} variant="warning" data-id="element-3077" />
        <StatsCard title="Promedio por Venta" value={formatCurrency(promedioVenta)} icon={<BarChart3Icon className="w-5 h-5" data-id="element-3080" />} variant="default" data-id="element-3079" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3081">
        {/* Sales list */}
        <Card padding="none" className="animate-stagger-2" data-id="element-3082">
          <div className="px-6 py-5 border-b border-slate-100" data-id="element-3083">
            <CardHeader title="Historial de Ventas" subtitle={`${salesVisits.length} ventas registradas`} data-id="element-3084" />
          </div>

          {salesVisits.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-3085">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3086">
                <ShoppingCartIcon className="w-8 h-8 text-slate-400" data-id="element-3087" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-3088">
                No hay ventas
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-3089">
                No hay ventas registradas en el período seleccionado
              </p>
            </div> : <>
              <div className="divide-y divide-slate-100" data-id="element-3090">
                {paginatedSales.map(visit => {
              const montoTotal = visit.venta.montoVenta;
              const subTotal = Math.round(montoTotal / 1.18 * 100) / 100;
              const itbis = Math.round((montoTotal - subTotal) * 100) / 100;
              return <div key={visit.id} className="px-6 py-4 hover:bg-slate-50/50 transition-colors" data-id="element-3091">
                      <div className="flex items-start justify-between" data-id="element-3092">
                        <div className="flex-1" data-id="element-3093">
                          <div className="flex items-center gap-3" data-id="element-3094">
                            <h4 className="font-semibold text-slate-900" data-id="element-3095">
                              {visit.clienteNombre}
                            </h4>
                            <Badge variant="info" data-id="element-3096">
                              {visit.venta.numeroPedido}
                            </Badge>
                          </div>
                          <div className="mt-1 flex items-center gap-4 text-sm text-slate-500" data-id="element-3097">
                            {isAdmin && <span className="font-medium" data-id="element-3098">
                                {visit.vendedorNombre}
                              </span>}
                            <span data-id="element-3099">{formatDate(visit.fecha)}</span>
                            <span data-id="element-3100">{visit.zona}</span>
                          </div>
                          {visit.comentarios && <p className="mt-2 text-sm text-slate-600" data-id="element-3101">
                              {visit.comentarios}
                            </p>}
                        </div>
                        <div className="text-right flex-shrink-0 ml-4" data-id="element-3102">
                          <p className="text-xl font-bold text-[#2952C4]" data-id="element-3103">
                            {formatCurrency(montoTotal)}
                          </p>
                          <div className="mt-1 text-xs text-slate-500 space-y-0.5" data-id="element-3104">
                            <p data-id="element-3105">
                              Sub Total:{' '}
                              <span className="font-semibold text-slate-700" data-id="element-3106">
                                {formatCurrency(subTotal)}
                              </span>
                            </p>
                            <p data-id="element-3107">
                              ITBIS 18%:{' '}
                              <span className="font-semibold text-slate-600" data-id="element-3108">
                                {formatCurrency(itbis)}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
              <div className="animate-stagger-3" data-id="element-3109">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={salesVisits.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-3110" />
              </div>
            </>}
        </Card>

        {/* Sales by vendor (admin only) */}
        {isAdmin && <Card data-id="element-3111">
            <CardHeader title="Ventas por Vendedor" data-id="element-3112" />
            <div className="mt-4 space-y-4" data-id="element-3113">
              {Object.entries(salesByVendor).sort((a, b) => b[1].total - a[1].total).map(([vendedor, data]) => <div key={vendedor} className="p-4 bg-slate-50 rounded-xl" data-id="element-3114">
                    <div className="flex items-center justify-between mb-2" data-id="element-3115">
                      <div data-id="element-3116">
                        <p className="font-semibold text-slate-900" data-id="element-3117">
                          {vendedor}
                        </p>
                        <p className="text-sm text-slate-500" data-id="element-3118">
                          {data.count} pedidos
                        </p>
                      </div>
                      <p className="text-lg font-bold text-[#2952C4]" data-id="element-3119">
                        {formatCurrency(data.total)}
                      </p>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-3120">
                      <div className="bg-[#2952C4] h-2 rounded-full transition-all" style={{
                width: `${data.total / Math.max(...Object.values(salesByVendor).map(d => d.total)) * 100}%`
              }} data-id="element-3121" />
                    </div>
                  </div>)}
            </div>
          </Card>}
      </div>
    </div>;
}